import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Inject,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import type { MatDatepicker } from '@angular/material/datepicker';
import { ValidationError } from 'validation';
import { ValidationService } from './../validation/validation.service';
import { Control } from './../control/control';
import { VirtualKeyboardService } from '../virtual-keyboard/virtual-keyboard.service';
import { DatePickerType } from '../model/features-impl';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';

@Component({
  selector: 'i-object-input',
  templateUrl: './object-input.component.html',
  styleUrls: ['./object-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectInputComponent extends Control implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() inpPath: string;
  @Input() inpPathForValidation: string;
  @Input() inpModel: string | null;
  @Input() inpParse: (value: string | null) => string | null;
  @Input() inpFormat: (value: string | null, monthFormat: 'letters' | null) => string | null;
  @Input() datePickerType: DatePickerType = DatePickerType.None;
  @Input() inpClass: string = '';
  @Input() inpLabel: string;
  @Input() inpTabIndex: number = 0;
  @Input() inpDisabled: boolean = false;
  @Input() inpMinWidth: number = 250;
  @Input() inpKeyboardLangualge: 'ru' | 'en' | null = null;
  @Input() inpMonthFormat: 'letters' | null = null;
  @Output() inpModelChange = new EventEmitter<string | null>();

  keyboardInsideScroll: string = '';
  readonly appearance?: MatFormFieldDefaultOptions['appearance'];

  private validationIndex: number;
  private dirty: boolean = false;
  private model0: string | null = null;

  @ViewChild('self') private self: ElementRef<HTMLInputElement>;
  @ViewChild('forPicker') forPicker: ElementRef<HTMLInputElement>;
  @ViewChild('picker') picker: MatDatepicker<moment.Moment>;

  constructor(
    private cdr: ChangeDetectorRef,
    private validationService: ValidationService,
    private virtualKeyboardService: VirtualKeyboardService,
    @Inject(MAT_FORM_FIELD_DEFAULT_OPTIONS) private options: MatFormFieldDefaultOptions,
  ) {
    super();
    this.appearance = this.options.appearance;
  }

  ngOnInit() {
    if (this.virtualKeyboardService.getInsideScroll()) {
      this.keyboardInsideScroll = this.virtualKeyboardService.getHeight() + 'px';
    }
    const validationRegistration = this.validationService.register({
      path: this.inpPathForValidation,
      pathExact: true,
      consumer: (final: boolean, errors: ValidationError[]) => {
        if (final || this.dirty) {
          this.decorate(errors);
          this.showCurrentTooltip();
          this.cdr.markForCheck();
        }
      },
      getElementForFocus: () => this.getElementForFocus(),
    });
    this.validationIndex = validationRegistration.regKey;
    this.dirty = validationRegistration.dirty;
  }

  ngAfterViewInit() {
    this.convertModelToDatePicker();
    if (this.picker) {
      this.picker.xPosition = 'end';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    let inpModelChange = changes['inpModel'];
    if (inpModelChange) {
      if (!inpModelChange.isFirstChange()) {
        this.dirty = true;
        this.convertModelToDatePicker();
        this.validationService.validate();
      }
    }
  }

  ngOnDestroy() {
    this.validationService.unregister(this.validationIndex, this.dirty);
  }

  focused(self: HTMLInputElement, tooltip: MatTooltip) {
    this.model0 = this.inpModel;
    this.showTooltip(tooltip);
    this.virtualKeyboardService.focus(
      self,
      (val: string) => {
        this.inpModel = val;
      },
      (dir: boolean) => {
        this.validationService.navigate(self, dir);
      },
      () => {
        if (document.activeElement === self) {
          self.scrollIntoView({ block: 'center' });
        }
      },
      this.inpKeyboardLangualge,
    );
  }

  private updateInputModel() {
    if (this.model0 !== this.inpModel) {
      this.convertModelToDatePicker();
      this.inpModel = this.inpFormat(this.inpParse(this.inpModel), this.inpMonthFormat);
      this.inpModelChange.emit(this.inpModel);
    }
  }

  blurred() {
    this.updateInputModel();
    this.hideTooltip();
    this.virtualKeyboardService.blur();
  }

  onKeyUp(): void {
    this.updateInputModel();
  }

  convertModelToDatePicker() {
    if (this.forPicker && this.datePickerType && this.inpModel !== null) {
      const text = this.inpParse(this.inpModel);
      if (text) {
        this.forPicker.nativeElement.value = text.replace('-00-', '-01-').replace('-00', '-01');
      } else {
        this.forPicker.nativeElement.value = '';
      }
      this.forPicker.nativeElement.dispatchEvent(new Event('input'));
    }
  }

  private convertDatePickerToModel(value: moment.Moment) {
    let text = '';
    switch (this.datePickerType) {
      case DatePickerType.YMD:
        text = value.format('DD MM YYYY');
        break;
      case DatePickerType.YM:
        text = value.format('MM YYYY');
        break;
      case DatePickerType.Y:
        text = value.format('YYYY');
        break;
    }
    this.inpModel = this.inpFormat(this.inpParse(text), this.inpMonthFormat);
    this.inpModelChange.emit(this.inpModel);
  }

  dpDaySelected(event: { value: moment.Moment }) {
    this.convertDatePickerToModel(event.value);
  }

  dpMonthSelected(event: moment.Moment, dp: MatDatepicker<moment.Moment>) {
    if (this.datePickerType == DatePickerType.YM) {
      this.convertDatePickerToModel(event);
      this.convertModelToDatePicker();
      dp.close();
    }
  }

  dpYearSelected(event: moment.Moment, dp: MatDatepicker<moment.Moment>) {
    if (this.datePickerType == DatePickerType.Y) {
      this.convertDatePickerToModel(event);
      this.convertModelToDatePicker();
      dp.close();
    }
  }

  private getElementForFocus(): HTMLElement | null {
    return this.inpDisabled ? null : this.self.nativeElement;
  }

  focus() {
    const element = this.getElementForFocus();
    if (element) {
      element.focus();
    }
  }
}
